import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbMarkdownEditor;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbMarkdownPersonAutocomplete } from "abreact/input/markdown/AbMarkdownPersonAutocomplete";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { AbMarkdownImage } from "abreact/input/markdown/AbMarkdownImage";
import { AbMarkdownEditorUtils } from "abreact/input/markdown/AbMarkdownEditorUtils";
import { AbMarkdownImageDialog } from "abreact/input/markdown/AbMarkdownImageDialog";
import { abMarkdownImagePlugin } from "abreact/input/markdown/AbMarkdownImagePlugin";
import { AbMarkdownPersonCmp } from "abreact/input/markdown/AbMarkdownPersonCmp";
import { abMarkdownPersonPlugin } from "abreact/input/markdown/AbMarkdownPersonPlugin";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbLocalToolbar, AbLocalToolbarGroup, AbLocalToolbarSection } from "abreact/incubator/ui/toolbar/AbLocalToolbar";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { abMarkdownColorPlugin } from "abreact/input/markdown/AbMarkdownColorPlugin";
import { AbMarkdownLink } from "abreact/input/markdown/AbMarkdownLink";
import { AbMarkdownLinkDialog } from "abreact/input/markdown/AbMarkdownLinkDialog";
import { AbColorPicker } from "abreact/misc/AbColorPicker";
import { AbButton } from "abreact/misc/AbButton";
import { AbToggleButton } from "absdk/commons/ui/misc/AbToggleButton";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import * as Draft from "draft-js";
import { draftToMarkdown, markdownToDraft } from "markdown-draft-js";
import * as React from "react";
import { AbAutoSizer } from "absdk/commons/ui/layout/AbAutoSizer";
import { AbTextArea } from "abreact/input/AbTextArea";
import TextareaMarkdown from "textarea-markdown-editor";
import { isURL } from "textarea-markdown-editor/dist/utils";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbDialog } from "abreact/popup/AbDialog";
import { AbMenuButton } from "abreact/menu/AbMenuButton";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { DISABLE_DEPRECATION, getDeprecatedImageStyle, getDeprecatedTooltip } from "abreact/misc/AbDeprecatedIndicator";

// { EditorState, Editor, convertToRaw, DraftEditorCommand, DraftHandleValue, RichUtils, DraftBlockType, DraftInlineStyle, DraftInlineStyleType, ContentBlock, SelectionState, Modifier }

export const AbMarkdownEditorTools = ["bold", "italic", "underline", "strikeThrough", "monospace",
// text tools
"bulleted", "numbered",
// list tools
"color", "background",
// color tools
"link", "image",
// entity tools
"textMode",
// show source code
"popup" // in floating toolbar, allow popup dialog
];
/** commands generated by keys that control the editor */
export let AbMarkdownCommands = /*#__PURE__*/function (AbMarkdownCommands) {
  AbMarkdownCommands["ENTER_AUTOCOMPLETE_MODE"] = "enter-autocomplete-mode";
  // start an autocomplete with current persons
  AbMarkdownCommands["AUTOCOMPLETE_UP_ARROW"] = "autocomplete-up-arrow";
  // change selection in current autocomplete
  AbMarkdownCommands["AUTOCOMPLETE_DOWN_ARROW"] = "autocomplete-down-arrow";
  // change selection in current autocomplete
  AbMarkdownCommands["AUTOCOMPLETE_ENTER"] = "autocomplete-enter";
  // convert current autocomplete to person, add to editor, close autocomplete
  AbMarkdownCommands["AUTOCOMPLETE_EXIT"] = "autocomplete-exit";
  // convert current autocomplete back to text, close autocomplete
  AbMarkdownCommands["HANDLED"] = "handled";
  // do nothing else, consider the key handled
  AbMarkdownCommands["INSERT_NEWLINE"] = "insert-newline";
  AbMarkdownCommands["ARROW_LEFT"] = "arrow-left";
  // the arrow keys don't work well, turn them into commands
  AbMarkdownCommands["ARROW_RIGHT"] = "arrow-right";
  // the arrow keys don't work well, turn them into commands
  AbMarkdownCommands["ARROW_UP"] = "arrow-up";
  AbMarkdownCommands["ARROW_DOWN"] = "arrow-down";
  AbMarkdownCommands["END"] = "end";
  return AbMarkdownCommands;
}({});
/**
 * Bring up a markdown editor
 * @deprecated Use {@link AbMarkdownLexical} instead
 */
export class AbMarkdownEditor extends React.Component {
  constructor(props) {
    super(props);
    // Markdown representing the content of the DraftEditor.  Not in state, deliberately.
    _defineProperty(this, "currentMarkdown", "");
    _defineProperty(this, "placeholder", void 0);
    // should we set the component to focus after the next render?
    _defineProperty(this, "focusOnComponentUpdate", false);
    _defineProperty(this, "textareaMarkdownRef", void 0);
    _defineProperty(this, "imageButtonRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "linkButtonRef", /*#__PURE__*/React.createRef());
    // The Draft.Editor, set by a ref
    _defineProperty(this, "draftEditor", null);
    _defineProperty(this, "personExtension", undefined);
    _defineProperty(this, "persistImageExtension", undefined);
    _defineProperty(this, "autocompleteCurrentSelection", void 0);
    _defineProperty(this, "autocompleteRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "utils", void 0);
    _defineProperty(this, "decorator", void 0);
    _defineProperty(this, "onEditorStateChange", editorState => {
      this.setState({
        editorState
      }, () => {
        this.currentMarkdown = this.getMarkdownText();
        if (this.props.value !== this.currentMarkdown) {
          this.updatePlaceholder(this.currentMarkdown);
          this.props.onChange(this.currentMarkdown);
        }
      });
    });
    _defineProperty(this, "enterAutocompleteMode", () => {
      let newState;
      this.setState({
        autocompleteMode: true
      });
      const contentState = this.state.editorState.getCurrentContent();
      const selection = this.state.editorState.getSelection();
      const contentStateWithEntity = contentState.createEntity('AUTOCOMPLETE_MARKER', 'MUTABLE');
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      // make a 2-character entity containing "@" and a 0-width-space, so the cursor can be between them
      const newContentState = Draft.Modifier.insertText(contentStateWithEntity, selection, "@\u200b", undefined, entityKey);
      newState = Draft.EditorState.push(this.state.editorState, newContentState, "apply-entity");

      // put the selection between the @ and the 0-width space.  Next characters typed will have the AUTOCOMPLETE_MARKER entity key
      let newSelection = Draft.SelectionState.createEmpty(selection.getAnchorKey());
      newSelection = newSelection.merge({
        anchorOffset: selection.getStartOffset() + 1,
        focusKey: selection.getAnchorKey(),
        focusOffset: selection.getStartOffset() + 1
      });
      return Draft.EditorState.forceSelection(newState, newSelection);
    });
    /** Leave autocomplete mode
     * Public because it's called from AbMarkdownPersonAutocomplete.
     * @parameter person if set, the person link to insert; otherwise autocomplete text becomes plain text.
     */
    _defineProperty(this, "exitAutocomplete", (person, editorState) => {
      let selection = editorState.getSelection();
      const blockKey = selection.getAnchorKey();
      let contentState = editorState.getCurrentContent();
      const currentBlock = contentState.getBlockForKey(blockKey);
      let text = "";
      let anchorOffset = -1;
      let focusOffset = -1;

      // Find the autocomplete marker range
      currentBlock.findEntityRanges(
      // scan the current block marker
      character => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "AUTOCOMPLETE_MARKER";
      }, (start, end) => {
        text = currentBlock.getText().substring(start, end);
        anchorOffset = start;
        focusOffset = end;
      });
      AbMarkdownEditor.LOG.debug(`exitAutocomplete anchorOffset:${anchorOffset} focusOffset:${focusOffset} text:${text} person:${JSON.stringify(person)}`);
      if (anchorOffset < 0 || focusOffset < 0) return; // couldn't find marker, so we're done

      // Set the selection to the AUTOCOMPLETE_MARKER range we found, which includes the @ and the whitespace
      selection = Draft.SelectionState.createEmpty(selection.getAnchorKey());
      selection = selection.merge({
        anchorKey: selection.getAnchorKey(),
        anchorOffset: anchorOffset,
        focusKey: selection.getAnchorKey(),
        focusOffset: focusOffset
      });
      let newText; // visible text that's going in, either as plaintext or as a person
      if (person) {
        // replace the marker with a person
        contentState = contentState.createEntity("PERSON", "IMMUTABLE", {
          id: person.id,
          displayName: person.displayName
        });
        const entityKey = contentState.getLastCreatedEntityKey();
        // make a new EditorState with the new content
        newText = person.displayName || "\u200b"; // if no name, use narrow space
        contentState = Draft.Modifier.replaceText(contentState, selection, newText); // removes the autocomplete entity, replaces with person name
        // set selection to the new text
        selection = selection.merge({
          focusOffset: selection.getAnchorOffset() + newText.length
        });
        contentState = Draft.Modifier.applyEntity(contentState, selection, entityKey); // creates the person entity on the person name
      } else {
        // replace the marker with plaintext, which is just the autocomplete contents including the @, but not the narrow space
        newText = text.substring(0, text.length - 1);
        contentState = Draft.Modifier.replaceText(contentState, selection, newText);
      }
      let newEditorState = Draft.EditorState.push(editorState, contentState, "change-block-data");

      // move selection to end of what just got inserted
      selection = selection.merge({
        anchorOffset: selection.getAnchorOffset() + newText.length,
        focusOffset: selection.getAnchorOffset() + newText.length
      });
      newEditorState = Draft.EditorState.forceSelection(newEditorState, selection);
      this.setState({
        autocompleteMode: false
      });
      this.onEditorStateChange(newEditorState);
    });
    // After Draft converts keystrokes to commands, this function handles them.  Most are built-in; see Draft.RichUtils.handleKeyCommand.
    _defineProperty(this, "handleKeyCommand", (command, editorState) => {
      let newState;
      switch (command) {
        case AbMarkdownCommands.ENTER_AUTOCOMPLETE_MODE:
          newState = this.enterAutocompleteMode();
          break;
        case AbMarkdownCommands.AUTOCOMPLETE_ENTER:
          this.exitAutocomplete(this.autocompleteCurrentSelection, editorState);
          return "handled";
        case AbMarkdownCommands.AUTOCOMPLETE_EXIT:
          this.exitAutocomplete(undefined, editorState);
          return "handled";
        case AbMarkdownCommands.AUTOCOMPLETE_DOWN_ARROW:
          this.autocompleteRef.current?.incrementSelection?.(1);
          return "handled";
        case AbMarkdownCommands.AUTOCOMPLETE_UP_ARROW:
          this.autocompleteRef.current?.incrementSelection?.(-1);
          return "handled";
        case AbMarkdownCommands.HANDLED:
          return "handled";
        case AbMarkdownCommands.INSERT_NEWLINE:
          newState = Draft.RichUtils.insertSoftNewline(editorState);
          break;
        case AbMarkdownCommands.ARROW_LEFT:
          newState = this.utils?.moveSelectionBackward(editorState) ?? null;
          break;
        case AbMarkdownCommands.ARROW_RIGHT:
          newState = this.utils?.moveSelectionForward(editorState) ?? null;
          break;
        case AbMarkdownCommands.END:
          newState = this.utils?.moveSelectionToEndOfLine(editorState) ?? null;
          break;
        default:
          newState = Draft.RichUtils.handleKeyCommand(editorState, command);
      }
      if (newState) {
        this.onEditorStateChange(newState);
        return "handled";
      }
      return "not-handled";
    });
    // optionally add a classname to draft blocks
    _defineProperty(this, "blockStyleFn", block => {
      if (block.getType() === "unstyled") return "draft-block-unstyled";
      if (block.getType() === "code-block") return "draft-block-code-block";
      return null;
    });
    _defineProperty(this, "customStyleFn", (style, block) => {
      const styleNames = style.toJS(); // convert to array of styles
      let styleName;
      let retVal = {};
      for (styleName of styleNames) {
        if (styleName.startsWith("COLOR_")) {
          retVal.color = styleName.split("COLOR_")[1];
        }
        if (styleName.startsWith("BACKGROUND_")) {
          retVal.backgroundColor = styleName.split("BACKGROUND_")[1];
        }
      }
      return retVal;
    });
    _defineProperty(this, "lastKey", null);
    _defineProperty(this, "keyBindingFn", e => {
      this.lastKey = e.key;
      const defaultCommand = Draft.getDefaultKeyBinding(e);
      let command = undefined;
      if (this.state.autocompleteMode) {
        if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
          command = this.autocompleteRef.current?.keyEvent?.(e); // send key to autocomplete. It may close, or allow key to fall through.
        } else if (defaultCommand === "delete" || defaultCommand === "backspace") {
          command = this.autocompleteRef.current?.keyCommand?.(defaultCommand);
        } else {
          command = AbMarkdownEditor.autocompleteKeyCommandMap[e.code];
          if (!command && defaultCommand) {
            return null; // don't allow other default commands to happen: e.g., bold, italic, split-block, undo, redo
          }
        }
        if (command) {
          e.stopPropagation();
          return command;
        }
      }

      // "@" key does nothing until you have a MarkdownPersonExtension
      if (this.personExtension && e.key === "@") {
        return AbMarkdownCommands.ENTER_AUTOCOMPLETE_MODE;
      }
      if (e.code === "Enter" && e.shiftKey) {
        return AbMarkdownCommands.INSERT_NEWLINE;
      }
      return defaultCommand;
    });
    /**
     * Switch between textarea mode and WYSIWYG mode.
     */
    _defineProperty(this, "editMarkdown", showTextareaMarkdown => {
      if (showTextareaMarkdown) {
        this.setState({
          showTextareaMarkdown,
          unsavedTextareaMarkdown: this.getMarkdownText()
        });
      } else {
        this.setNewMarkdown(this.state.unsavedTextareaMarkdown);
        this.setState({
          showTextareaMarkdown
        });
      }
    });
    // ABSDK-3179: call the onchange method, even in text mode
    _defineProperty(this, "onChangeTextarea", text => {
      this.setState({
        unsavedTextareaMarkdown: text
      });
      this.currentMarkdown = text;
      if (this.props.value !== this.currentMarkdown) {
        this.updatePlaceholder(this.currentMarkdown);
        this.props.onChange(this.currentMarkdown);
      }
    });
    _defineProperty(this, "hideDialogs", () => {
      this.setState({
        showLinkDialog: false,
        showImageDialog: false
      });
    });
    _defineProperty(this, "fileReader", void 0);
    _defineProperty(this, "handlePastedFiles", files => {
      let retVal = 'handled';
      this.fileReader.readAsDataURL(files[0]);
      return retVal;
    });
    _defineProperty(this, "handleDroppedFiles", (selection, files) => {
      const editorState = this.state.editorState;
      const newEditorState = Draft.EditorState.forceSelection(editorState, selection);
      this.onEditorStateChange(newEditorState);
      let retVal = 'handled';
      this.fileReader.readAsDataURL(files[0]);
      return retVal;
    });
    _defineProperty(this, "insertImage", async (alt, url) => {
      // check that the URL is valid.  If not, inform caller that it didn't insert
      try {
        const test = new URL(url);
      } catch (e) {
        return Promise.resolve(false);
      }
      if (url.startsWith("data:") && this.persistImageExtension) {
        url = await this.persistImageExtension.persistImage(url);
      }
      const contentState = this.state.editorState.getCurrentContent();
      const selection = this.state.editorState.getSelection();
      let newContentState;

      //  If selection contains an image, replace the first one in the selection.
      let entityKey = this.getEntityKeyNearSelection();
      if (entityKey) {
        // update existing image
        newContentState = contentState.replaceEntityData(entityKey, {
          alt: alt,
          src: url
        });
      } else {
        // new image, inserted at the anchor.
        let contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', {
          alt: alt,
          src: url
        });
        entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        // if necessary, collapse the selection to the anchor
        let collapsedSelection;
        if (selection.isCollapsed()) collapsedSelection = selection;else {
          const temp = Draft.SelectionState.createEmpty(selection.getAnchorKey());
          collapsedSelection = temp.merge({
            anchorKey: selection.getAnchorKey(),
            anchorOffset: selection.getAnchorOffset(),
            focusKey: selection.getAnchorKey(),
            focusOffset: selection.getAnchorOffset()
          });
        }
        newContentState = Draft.Modifier.insertText(contentStateWithEntity, collapsedSelection, "\u200b", undefined, entityKey);
      }
      // force a re-render, by calling forceSelection in the same spot!
      const forcedState = Draft.EditorState.forceSelection(this.state.editorState, selection);
      const pushedEditorState = Draft.EditorState.push(forcedState, newContentState, 'apply-entity');
      this.onEditorStateChange(pushedEditorState);
      return true;
    });
    /** Like Draft.ContentState.createEntity based on current editorState */
    _defineProperty(this, "createEntity", (type, mutability, data) => {
      const contentState = this.state.editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(type, mutability, data);
      const key = contentStateWithEntity.getLastCreatedEntityKey();
      return contentState.getEntity(key);
    });
    /** https://github.com/Rosey/markdown-draft-js is a simple library that converts markdown to raw draft.
     * That library one entry, `markdownToDraft`, which has two args: `{ markdown: string, options: markdownToDraftOptions }`.
     * This is the second arg.  For details, see the readme on github. */
    _defineProperty(this, "markdownToDraftOptions", {
      blockEntities: {
        // markdownToDraft stores the return value of "foo_open" and attaches it to the last character on "foo_close."
        // I wrote abMarkdownImagePlugin to generate three tokens: <image_open, white space character, image_close>.
        // Thus, the image_close token will attach the entity to the whitespace character.
        image_open: child => {
          if (!child) {
            throw new Error("markdown editor: unexpected null value: image_open");
          }
          return this.createEntity("IMAGE", "IMMUTABLE", {
            src: child.content.src,
            alt: child.content.alt
          });
        },
        link_open: child => {
          if (!child) {
            throw new Error("markdown editor: unexpected null value: link_open");
          }
          const url = child.href;
          const protocol = URLUtils.getProtocol(url);
          const extension = protocol ? AbMarkdownExtensions.markdownLinkExtensions.get(protocol) : undefined;
          return this.createEntity("LINK", extension ? "IMMUTABLE" : "MUTABLE",
          // don't allow extension link to be edited
          {
            url: url,
            href: url
          });
        },
        person_open: child => {
          if (!child) {
            throw new Error("markdown editor: unexpected null value: person_open");
          }
          return this.createEntity("PERSON", "IMMUTABLE", {
            id: child.content.id,
            displayName: child.content.displayName
          });
        }
      },
      blockStyles: AbMarkdownEditor.markdownToDraftBlockStyles,
      remarkablePlugins: [abMarkdownColorPlugin, abMarkdownPersonPlugin, abMarkdownImagePlugin],
      remarkableOptions: {
        enable: {
          inline: ["ins", "del"]
        }
      }
    });
    /**
     * Change the current draft editor to contain new markdown.
     * Called from the constructor and when the textarea is hidden.
     * @param newMarkdown
     * @param moveFocusToEnd if true, the cursor will be at the end of the content
     */
    _defineProperty(this, "setNewMarkdown", (newMarkdown, moveFocusToEnd) => {
      // convert newMarkdown to Draft
      const rawData = markdownToDraft(newMarkdown, this.markdownToDraftOptions);
      const contentState = Draft.convertFromRaw(rawData);
      let newEditorState = Draft.EditorState.push(this.state.editorState, contentState, "change-block-data");
      if (moveFocusToEnd) {
        newEditorState = Draft.EditorState.moveFocusToEnd(newEditorState);
      }
      this.currentMarkdown = newMarkdown;
      this.setState({
        editorState: newEditorState
      });
      if (this.props.value !== newMarkdown) {
        // tell the container
        this.updatePlaceholder(newMarkdown);
        this.props.onChange(newMarkdown);
      }
    });
    _defineProperty(this, "editPerson", () => {
      let entityKey = this.getEntityKeyNearSelection();
      if (!entityKey) return;
      let selection = this.state.editorState.getSelection();
      if (selection.getAnchorKey() !== selection.getFocusKey()) {
        return; // we can't do links across blocks
      }
      const blockKey = selection.getAnchorKey();
      let contentState = this.state.editorState.getCurrentContent();
      const currentBlock = contentState.getBlockForKey(blockKey);
      let startLink = -1;
      let endLink = -1;
      currentBlock.findEntityRanges(
      // scan the current block for entities...
      character => {
        return character.getEntity() === entityKey;
      }, (start, end) => {
        startLink = start;
        endLink = end;
      });
      if (startLink < 0 || endLink < 0) return; // couldn't find person entity
      selection = selection.merge({
        anchorOffset: startLink,
        focusOffset: endLink
      });
      let text = currentBlock.getText().substring(startLink, endLink);
      const contentStateWithEntity = contentState.createEntity('AUTOCOMPLETE_MARKER', 'MUTABLE');
      entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      // make an autocomplete entity containing the text (and usual wrapper)
      const newContentState = Draft.Modifier.replaceText(contentStateWithEntity, selection, "@" + text + "\u200b", undefined, entityKey); // Go into autocomplete mode

      let newState = Draft.EditorState.push(this.state.editorState, newContentState, "apply-entity");

      // put the selection between the end of text and the 0-width space.
      let newSelection = Draft.SelectionState.createEmpty(selection.getAnchorKey());
      newSelection = newSelection.merge({
        anchorOffset: endLink + 1,
        focusKey: selection.getAnchorKey(),
        focusOffset: endLink + 1
      });
      newState = Draft.EditorState.forceSelection(newState, newSelection);
      this.setState({
        autocompleteMode: true
      });
      this.onEditorStateChange(newState);
    });
    /**
     * Bring up the Edit Image dialog.
     * The dialog's contents:
     * - if an image is under the cursor, then the image's alt and URL.
     * - Otherwise, alt and URL are blank
     * Public because it's called from an image's popup menu
     */
    _defineProperty(this, "editImage", () => {
      if (this.state.showTextareaMarkdown) {
        // Textarea editor showing
        const textAreaMarkdownRef = this.textareaMarkdownRef.current;
        if (!textAreaMarkdownRef) return;
        // Is cursor inside an image?  Popup the image dialog for it.
        const currentPosition = textAreaMarkdownRef.cursor.position;
        const currentColumn = currentPosition.cursorAt - currentPosition.line.startsAt; // where in the line is the cursor?
        const imageRegex = RegExp(/!\[([^\]]*)]\(([^)]*)\)/, "g");
        let matched;
        while ((matched = imageRegex.exec(currentPosition.line.text)) !== null) {
          if (matched.index < currentColumn && matched.index + matched[0].length > currentColumn) {
            const alt = matched[1];
            const url = matched[2];
            this.setState({
              showImageDialog: true,
              image: {
                alt,
                url
              }
            });
            return;
          }
        }
        // If cursor is on an url, use that as the href for the image
        if (isURL(textAreaMarkdownRef.cursor.selection?.text)) {
          const linkText = textAreaMarkdownRef.cursor.selection.text;
          const MARKER = "\0";
          textAreaMarkdownRef.cursor.insert(`![${MARKER}alt${MARKER}](${linkText})`);
          return;
        }
        // Otherwise, do what the textarea component says
        textAreaMarkdownRef.trigger("image");
        return;
      }
      // DraftJS editor is showing
      let entityKey = this.getEntityKeyNearSelection();
      let url = "";
      let alt = "";
      if (entityKey) {
        const contentState = this.state.editorState.getCurrentContent();
        const entityInstance = contentState.getEntity(entityKey);
        url = entityInstance.getData().src;
        alt = entityInstance.getData().alt;
        this.imageButtonRef = /*#__PURE__*/React.createRef(); // on existing image, so center the dialog
      }
      this.setState({
        showImageDialog: true,
        image: {
          alt,
          url
        }
      });
    });
    /**
     * Bring up the Edit Link dialog.
     * The dialog's contents:
     *    - if a link is under the cursor, then the link's text and URL.
     *    - Otherwise, text is the current selection, and the URL is blank
     * Public because it's called from a link's popup menu
     */
    _defineProperty(this, "editLink", () => {
      if (this.state.showTextareaMarkdown) {
        // Textarea editor showing
        const textAreaMarkdownRef = this.textareaMarkdownRef.current;
        if (textAreaMarkdownRef) {
          // Is cursor inside a link?  Popup the link dialog for it.
          const currentPosition = textAreaMarkdownRef.cursor.position;
          const currentColumn = currentPosition.cursorAt - currentPosition.line.startsAt; // where in the line is the cursor?
          const linkRegex = RegExp(/\[([^\]]*)]\(([^)]*)\)/, "g");
          let matched;
          while ((matched = linkRegex.exec(currentPosition.line.text)) !== null) {
            if (matched.index < currentColumn && matched.index + matched[0].length > currentColumn) {
              const text = matched[1];
              const url = matched[2];
              this.setState({
                showLinkDialog: true,
                link: {
                  text,
                  url
                }
              });
              return;
            }
          }
          // if cursor is on an url, use that as both the selection and the text
          if (isURL(textAreaMarkdownRef.cursor.selection?.text)) {
            const linkText = textAreaMarkdownRef.cursor.selection.text;
            const MARKER = "\0";
            textAreaMarkdownRef.cursor.insert(`[${MARKER}${linkText}${MARKER}](${linkText})`);
            return;
          }
          // otherwise, do what the textarea component says
          textAreaMarkdownRef.trigger("link");
        }
        return;
      }
      // DraftJS editor is showing
      const selection = this.state.editorState.getSelection();
      if (selection.getAnchorKey() !== selection.getFocusKey()) {
        return; // we can't do links across blocks
      }
      const blockKey = selection.getAnchorKey();
      let startOffset = selection.getStartOffset();
      let endOffset = selection.getEndOffset();
      let contentState = this.state.editorState.getCurrentContent();
      const currentBlock = contentState.getBlockForKey(blockKey);

      // If there's a link overlapping the selection, edit it
      let startLink = -1;
      let endLink = -1;
      currentBlock.findEntityRanges(
      // scan the current block for entities...
      character => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
      }, (start, end) => {
        if (startOffset > start && startOffset < end || endOffset > start && endOffset < end) {
          // Found one!
          startLink = start;
          endLink = end;
        }
      });
      if (startLink >= 0) {
        // expand the selection to the link we found
        let newSelection = Draft.SelectionState.createEmpty(blockKey);
        newSelection = newSelection.merge({
          focusKey: blockKey,
          anchorOffset: startLink,
          focusOffset: endLink
        });
        contentState = contentState.merge({
          selectionBefore: selection,
          selectionAfter: newSelection
        });
        const editorStateWithNewSelection = Draft.EditorState.push(this.state.editorState, contentState, "apply-entity");
        this.onEditorStateChange(editorStateWithNewSelection);
        startOffset = startLink;
        endOffset = endLink;
      }

      // Create a link from the (possibly newly expanded) selection. It might be empty, so it's an insert.
      const linkKey = currentBlock.getEntityAt(startOffset);
      let text = currentBlock.getText().slice(startOffset, endOffset);
      let url = "";
      if (linkKey) {
        // current selection is a link, so get existing url to populate the dialog
        const linkInstance = contentState.getEntity(linkKey);
        url = linkInstance.getData().url;
        this.linkButtonRef = /*#__PURE__*/React.createRef(); // existing link, so center the dialog
      }
      this.setState({
        showLinkDialog: true,
        link: {
          text,
          url
        }
      });
    });
    /**
     * Replace the selection with a link.
     * @param text - the content of the link to insert
     * @param url - the target of the link
     * @returns promise that resolves to true.
     */
    _defineProperty(this, "insertLink", (text, url) => {
      if (this.state.showTextareaMarkdown) {
        // Textarea editor showing, so cursor is on link to be replaced
        const textAreaMarkdownRef = this.textareaMarkdownRef.current;
        if (textAreaMarkdownRef) {
          const currentPosition = textAreaMarkdownRef.cursor.position;
          const currentColumn = currentPosition.cursorAt - currentPosition.line.startsAt; // where in the line is the cursor?
          const lineText = currentPosition.line.text;
          const linkRegex = RegExp(/\[[^\]]*]\([^)]*\)/, "g");
          let matched;
          while ((matched = linkRegex.exec(lineText)) !== null) {
            if (matched.index < currentColumn && matched.index + matched[0].length > currentColumn) {
              const newLink = `[${text}](${url})`;
              const newLine = lineText.slice(0, matched.index) + "\0" + newLink + "\0" + lineText.slice(matched.index + matched[0].length);
              textAreaMarkdownRef.cursor.replaceLine(currentPosition.line.lineNumber, newLine);
              this.focusOnComponentUpdate = true;
              break;
            }
          }
        }
        return Promise.resolve(true);
      }
      // DraftJS editor is showing
      const contentState = this.state.editorState.getCurrentContent();
      // Replace the text in the selection
      let currentSelection = this.state.editorState.getSelection();
      const contentStateReplaced = Draft.Modifier.replaceText(contentState, currentSelection, text);
      // Adjust the selection to the new content
      if (text.length !== currentSelection.getEndOffset() - currentSelection.getStartOffset()) {
        const newSelection = Draft.SelectionState.createEmpty(currentSelection.getAnchorKey());
        currentSelection = newSelection.merge({
          anchorOffset: currentSelection.getStartOffset(),
          focusKey: currentSelection.getAnchorKey(),
          focusOffset: currentSelection.getStartOffset() + text.length
        });
      }
      // Make a new Entity of type "LINK", allow it to be edited, and make its data contain the url.
      const contentStateWithEntity = contentStateReplaced.createEntity("LINK", "MUTABLE", {
        url
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      // make a new EditorState with the new content
      const newEditorState = Draft.EditorState.set(this.state.editorState, {
        currentContent: contentStateWithEntity
      });
      const contentStateWithLink = Draft.Modifier.applyEntity(newEditorState.getCurrentContent(), currentSelection, entityKey);
      const editorStateWithLink = Draft.EditorState.push(newEditorState, contentStateWithLink, "apply-entity");

      // tell the component
      this.focusOnComponentUpdate = true;
      this.onEditorStateChange(editorStateWithLink);
      this.onFocus();
      return Promise.resolve(true);
    });
    // The div element used to decide how wide we are.  This helps us choose narrow or wide toolbar.
    _defineProperty(this, "wrapperDiv", null);
    _defineProperty(this, "sizedDiv", null);
    // Put a blue highlight on the editor's eyelashes.
    _defineProperty(this, "onFocus", () => {
      this.sizedDiv?.classList.add("ab-focus");
      if (this.props.onFocus) {
        this.props.onFocus();
      }
    });
    // Remove the blue highlight from the editor's eyelashes.
    _defineProperty(this, "onBlur", () => {
      this.sizedDiv?.classList.remove("ab-focus");
      this.updatePlaceholder(this.currentMarkdown);
      if (this.props.onBlur) {
        this.props.onBlur();
      }
    });
    // Draft doesn't recognize an "image" as content, so sometimes it showed a placeholder next to image.
    // We think ANY markdown is content, even if it renders invisibly.
    _defineProperty(this, "updatePlaceholder", value => {
      this.placeholder = value ? undefined : this.props.placeholder;
    });
    // Move the focus to the end if the user clicks in the editor after the text.
    _defineProperty(this, "moveFocusToEnd", e => {
      const classname = e.target.className;
      if (classname === "DraftEditor-root" || classname === "public-DraftEditorPlaceholder-inner") {
        const editorState = Draft.EditorState.moveFocusToEnd(this.state.editorState);
        this.onFocus();
        this.setState({
          editorState
        });
      }
    });
    _defineProperty(this, "colorPopupButtonRef", /*#__PURE__*/React.createRef());
    // handle to the color popup.  Only set when color popup is in a child menu.
    _defineProperty(this, "textColor", "black");
    // default text color
    _defineProperty(this, "backgroundColor", "white");
    this.textareaMarkdownRef = /*#__PURE__*/React.createRef();
    this.autocompleteCurrentSelection = undefined;
    try {
      // Any extension registered?
      this.personExtension = AbMarkdownExtensions.markdownPersonExtension.get();
    } catch (e) {
      this.personExtension = undefined;
    }
    try {
      this.persistImageExtension = AbMarkdownExtensions.markdownPersistImageExtension.get();
    } catch (e) {
      this.persistImageExtension = undefined;
    }

    // Can't initialize these in static initializer, only after i18n service has started
    AbMarkdownEditor.textareaMarkdownOptions.linkTextPlaceholder = AbReactResources.markdownEditor_textAreaLinkTextPlaceholder();
    AbMarkdownEditor.textareaMarkdownOptions.linkUrlPlaceholder = AbReactResources.markdownEditor_textAreaLinkUrlPlaceholder();
    AbMarkdownEditor.draftToMarkdownOptions = {
      entityItems: {
        IMAGE: {
          open: () => {
            return "";
          },
          close: entity => {
            if (!entity) return "";
            return `![${entity.data.alt}](${entity.data.src})`;
          }
        },
        PERSON: {
          open: () => {
            return "[";
          },
          close: entity => {
            if (!entity) return "";
            return `](person:${entity.data.id})`;
          }
        }
      },
      styleItems: {
        UNDERLINE: {
          open: () => {
            return "++";
          },
          close: () => {
            return "++";
          }
        }
      }
    };
    AbColorPicker.addColorsToMarkdownOptions(AbMarkdownEditor.draftToMarkdownOptions);
    this.fileReader = new FileReader();
    this.fileReader.onload = this.fileLoaded.bind(this);
  }
  componentDidMount() {
    this.utils = new AbMarkdownEditorUtils(this);
    document.addEventListener("selectionchange", this.utils.selectionChanged);

    // The decorator scans the blocks, and for every "LINK" entity found, renders a AbMarkdownLink component.
    // And for every "IMAGE" entity, renders a AbMarkdownImage component.
    // Pass each component the value of "this" so each can see its containingEditor
    this.decorator = new Draft.CompositeDecorator([{
      strategy: AbMarkdownEditor.findLinkEntities,
      component: AbMarkdownLink,
      props: {
        containingEditor: this
      }
    }, {
      strategy: AbMarkdownEditor.findImageEntities,
      component: AbMarkdownImage,
      props: {
        containingEditor: this
      }
    }, {
      strategy: AbMarkdownEditor.findPersonEntities,
      component: AbMarkdownPersonCmp,
      props: {
        containingEditor: this
      }
    }, {
      strategy: AbMarkdownEditor.findAutocompleteEntities,
      component: AbMarkdownPersonAutocomplete,
      props: {
        containingEditor: this,
        onSelectionChange: value => this.autocompleteCurrentSelection = value,
        abRef: this.autocompleteRef
      }
    }]);
    this.setState({
      editorState: Draft.EditorState.createEmpty(this.decorator)
    }, () => {
      this.setNewMarkdown(this.props.value, this.props.forwardFocusRef != null);
    });
    this.updatePlaceholder(this.props.value);
  }
  componentWillUnmount() {
    if (this.utils) document.removeEventListener("selectionchange", this.utils.selectionChanged);
  }
  textStyle(newStyle) {
    if (this.state.showTextareaMarkdown) {
      const textAreaMarkdownRef = this.textareaMarkdownRef.current;
      if (textAreaMarkdownRef) {
        textAreaMarkdownRef.trigger(AbMarkdownEditor.draftStyleToTextAreaMarkdown[newStyle]);
      }
    } else {
      const editorState = this.state.editorState;
      const editorStateFocused = Draft.EditorState.forceSelection(editorState, editorState.getSelection()); // make sure has focus
      const editorStateStyled = Draft.RichUtils.toggleInlineStyle(editorStateFocused, newStyle);
      this.onEditorStateChange(editorStateStyled);
      this.onFocus();
    }
  }
  textBlock(blockType) {
    if (this.state.showTextareaMarkdown) {
      const textAreaMarkdownRef = this.textareaMarkdownRef.current;
      if (textAreaMarkdownRef) {
        textAreaMarkdownRef.trigger(AbMarkdownEditor.draftBlockToTextAreaMarkdown[blockType]);
      }
    } else {
      this.onEditorStateChange(Draft.RichUtils.toggleBlockType(this.state.editorState, blockType));
      this.onFocus();
    }
  }
  static getSelectedBlocks(contentState, startKey, endKey) {
    const isSameBlock = startKey === endKey;
    const startingBlock = contentState.getBlockForKey(startKey);
    const selectedBlocks = [startingBlock];
    if (!isSameBlock) {
      let blockKey = startKey;
      while (blockKey !== endKey) {
        const nextBlock = contentState.getBlockAfter(blockKey);
        selectedBlocks.push(nextBlock);
        blockKey = nextBlock.getKey();
      }
    }
    return selectedBlocks;
  }

  /**
   * Called to change the foreground or background color of the selected text.
   * @param newColor - color to change to
   * @param colorOrBackground - either the text color or the background color
   */
  colorPicked(newColor, colorOrBackground) {
    if (this.state.showTextareaMarkdown) {
      const textAreaMarkdownRef = this.textareaMarkdownRef.current;
      if (textAreaMarkdownRef) {
        const matches = newColor.match(/var\(--ab-setting-color-(.*)\)/);
        if (matches) newColor = matches[1];
        textAreaMarkdownRef.trigger("color", newColor, colorOrBackground);
      }
    } else {
      this.colorPopupButtonRef.current?.close(); // close the color pop up

      const prefix = colorOrBackground.toUpperCase() + "_";

      // clean colors from existing selection:
      let contentState = this.state.editorState.getCurrentContent();
      let currentSelection = this.state.editorState.getSelection();
      const startOffset = currentSelection.getStartOffset();
      const endOffset = currentSelection.getEndOffset();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const isSameBlock = startKey === endKey;
      const selectedBlocks = AbMarkdownEditor.getSelectedBlocks(contentState, startKey, endKey);
      let finalEditorState = this.state.editorState;
      selectedBlocks.forEach(block => {
        const currentBlockKey = block.getKey();
        let selectionStart;
        let selectionEnd;
        if (currentBlockKey === startKey) {
          selectionStart = startOffset;
          selectionEnd = isSameBlock ? endOffset : block.getText().length;
        } else if (currentBlockKey === endKey) {
          selectionStart = isSameBlock ? startOffset : 0;
          selectionEnd = endOffset;
        } else {
          selectionStart = 0;
          selectionEnd = block.getText().length;
        }

        // Walk all the styles in the current block, and remove any {color|background}
        let newContent = false;
        for (let offset = selectionStart; offset < selectionEnd; offset++) {
          let iStyle = block.getInlineStyleAt(offset);
          iStyle.forEach(style => {
            if (style?.startsWith(prefix) && style !== newColor) {
              newContent = true;
              const selectionState = new Draft.SelectionState({
                anchorKey: currentBlockKey,
                anchorOffset: offset,
                focusKey: currentBlockKey,
                focusOffset: offset + 1
              });
              contentState = Draft.Modifier.removeInlineStyle(contentState, selectionState, style);
              block = contentState.getBlockForKey(currentBlockKey);
            }
          });
        }
        if (newContent) {
          finalEditorState = Draft.EditorState.push(finalEditorState, contentState, "change-inline-style");
        }
      });
      finalEditorState = Draft.EditorState.forceSelection(finalEditorState, currentSelection);
      // Don't make an inline style with the default colors (text black, background white)
      if (prefix + newColor !== "COLOR_black" && prefix + newColor !== "BACKGROUND_white") {
        finalEditorState = Draft.RichUtils.toggleInlineStyle(finalEditorState, prefix + newColor);
      }
      this.focusOnComponentUpdate = true; // set focus after next render
      this.onEditorStateChange(finalEditorState);
      this.onFocus();
    }
  }
  static debug(obj) {
    /* eslint-disable abinitio/no-instanceof */
    if (obj instanceof Draft.EditorState) {
      console.log("Getting Content");
      obj = obj.getCurrentContent();
    }
    if (obj instanceof Draft.ContentState) {
      console.log("Content blocks:");
      let block = obj.getFirstBlock();
      while (block != null) {
        AbMarkdownEditor.debugBlock(block, 2);
        block = obj.getBlockAfter(block.getKey());
      }
    } else if (obj instanceof Draft.SelectionState) {
      console.log("Anchor Key: ", obj.getAnchorKey());
      console.log("Anchor Offset: ", obj.getAnchorOffset());
      console.log("Focus Key: ", obj.getFocusKey());
      console.log("Focus Offset: ", obj.getFocusOffset());
      console.log("Is Backward: ", obj.getIsBackward());
      console.log("Has focus:", obj.getHasFocus());
    }
  }
  static debugBlock(block, offset = 0) {
    console.log(" ".repeat(offset) + "Block: " + block.getKey() + " Text: " + block.getText());
    let characterList = block.getCharacterList();
    characterList.forEach(characterMetadata => {
      console.log(`${" ".repeat(offset + 2)}[${characterMetadata?.toString()}]`);
    });
  }
  /**
   * Convert the text in the dialog to a markdown string.
   * Public for testing only.  Clients should use onChange().
   */
  getMarkdownText() {
    const markdown = draftToMarkdown(Draft.convertToRaw(this.state.editorState.getCurrentContent()), AbMarkdownEditor.draftToMarkdownOptions);
    return markdown.replace(/\u200b/g, ""); // the RENDERED markdown can't have a zero-width space in it.
  }

  fileLoaded(event) {
    const src = this.fileReader.result;
    if (typeof src === "string") this.insertImage("", src);
  }
  setSelection(blockKey, offset) {
    const editorState = this.state.editorState;
    const selectionState = Draft.SelectionState.createEmpty(blockKey);
    const newSelectionState = selectionState.merge({
      anchorKey: blockKey,
      anchorOffset: offset,
      focusKey: blockKey,
      focusOffset: offset
    });
    const newEditorState = Draft.EditorState.forceSelection(editorState, newSelectionState);
    this.setState({
      editorState: newEditorState
    });
  }
  getEntityKeyNearSelection() {
    const selection = this.state.editorState.getSelection();
    const contentState = this.state.editorState.getCurrentContent();

    // limit search to the current block
    const currentBlock = contentState.getBlockForKey(selection.getAnchorKey());
    for (let offset = selection.getStartOffset(); offset <= selection.getEndOffset(); offset++) {
      const entityKey = currentBlock.getEntityAt(offset);
      if (entityKey) return entityKey;
    }
    return null;
  }

  /** Save the size changes in the selected image.
   * Public because it's called from the AbMarkdownImage component.
   * @param width
   * @param height
   */
  changeSize(width, height) {
    let entityKey = this.getEntityKeyNearSelection();
    if (entityKey) {
      const contentState = this.state.editorState.getCurrentContent();
      const entityInstance = contentState.getEntity(entityKey);
      let src = entityInstance.getData().src;
      const questionMarkPos = src.indexOf("?");
      let queryStringParameters = {};
      if (questionMarkPos >= 0) {
        const queryString = src.substring(questionMarkPos + 1);
        queryStringParameters = URLUtils.queryStringToObject(queryString);
      }
      if (width) queryStringParameters.width = width;else delete queryStringParameters.width;
      if (height) queryStringParameters.height = height;else delete queryStringParameters.height;
      src = URLUtils.addParamsToUrl(questionMarkPos > 0 ? src.substring(0, questionMarkPos) : src, queryStringParameters);
      const newContentState = contentState.mergeEntityData(entityKey, {
        src
      });
      const newEditorState = Draft.EditorState.push(this.state.editorState, newContentState, 'apply-entity');
      this.onEditorStateChange(newEditorState);
    }
    return null;
  }
  /**
   * Delete the link under the selection.
   * Public because it's called from the link's popup menu
   */
  deleteLink() {
    const currentSelectionState = this.state.editorState.getSelection();
    const startKey = currentSelectionState.getStartKey();
    const startOffset = currentSelectionState.getStartOffset();
    const currentContent = this.state.editorState.getCurrentContent();
    const currentContentBlock = currentContent.getBlockForKey(startKey);
    const entityWithCursor = currentContentBlock.getEntityAt(startOffset);
    const newSelection = Draft.SelectionState.createEmpty(startKey);
    let updatedSelection = null;
    currentContentBlock.findEntityRanges(character => {
      return character.getEntity() === entityWithCursor;
    }, (start, end) => {
      updatedSelection = newSelection.merge({
        anchorOffset: start,
        focusOffset: end
      });
    });
    if (updatedSelection == null) {
      // didn't find a link?  Oh well.
      return;
    }
    const removeEntity = Draft.Modifier.applyEntity(currentContent, updatedSelection, null);
    const updatedEditorState = Draft.EditorState.push(this.state.editorState, removeEntity, "apply-entity");
    this.focusOnComponentUpdate = true;
    this.onEditorStateChange(updatedEditorState);
  }
  render() {
    if (!this.state) {
      return null;
    }
    const {
      tabIndex,
      autoFocus,
      disabled,
      ...inputProps
    } = ReactUtils.inputProps("ab-markdown-editor", this.props, [this.props.resizable && "ab-resizable", !DISABLE_DEPRECATION && "ab-deprecated-1"]);
    const deprecatedTooltip = getDeprecatedTooltip(this.props.tooltip, {
      currentPath: "webcommons/abreact/client/webapp/abreact/input/AbMarkdownEditor.tsx",
      newPath: "webcommons/abreact/client/webapp/abreact/input/markdown/AbMarkdownLexical.tsx",
      contact: "@buxbaum"
    });

    /* not AbAutoSizer */
    return /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: deprecatedTooltip
    }, /*#__PURE__*/React.createElement("div", _extends({}, inputProps, {
      ref: x => this.wrapperDiv = x,
      onClick: this.moveFocusToEnd,
      style: {
        ...this.props.style,
        ...getDeprecatedImageStyle()
      }
    }), /*#__PURE__*/React.createElement(AbAutoSizer, {
      className: "ab-markdown-autosizer",
      renderer: (width, height) => /*#__PURE__*/React.createElement("div", {
        className: "ab-markdown-editor-sized",
        style: {
          height,
          width
        },
        ref: x => {
          this.sizedDiv = x;
          !this.state.componentHasMounted && this.setState({
            componentHasMounted: true
          });
        }
      }, this.state.componentHasMounted && (this.props.floatingToolbar ? this.renderFloatingToolbar(this.state.editorState) : this.renderToolbar(this.state.editorState)), !this.state.showTextareaMarkdown && /*#__PURE__*/React.createElement("div", {
        className: "draft-js-wrapper"
      }, /*#__PURE__*/React.createElement(Draft.Editor, {
        blockStyleFn: this.blockStyleFn,
        customStyleFn: this.customStyleFn,
        keyBindingFn: this.keyBindingFn,
        onChange: this.onEditorStateChange,
        handleKeyCommand: this.handleKeyCommand,
        handlePastedFiles: this.handlePastedFiles,
        handleDroppedFiles: this.handleDroppedFiles,
        editorState: this.state.editorState,
        readOnly: disabled,
        tabIndex: tabIndex,
        placeholder: this.placeholder,
        ref: x => {
          this.draftEditor = x;
          this.props.forwardFocusRef?.assignRef(x);
        },
        onFocus: this.onFocus,
        onBlur: this.onBlur
      })), this.state.showTextareaMarkdown && /*#__PURE__*/React.createElement(TextareaMarkdown.Wrapper, {
        commands: AbMarkdownEditor.textareaMarkdownCommands,
        options: AbMarkdownEditor.textareaMarkdownOptions,
        ref: this.textareaMarkdownRef
      }, /*#__PURE__*/React.createElement(AbTextArea, {
        className: "ab-markdown-textarea",
        dataTestId: "edit-markdown-textarea",
        value: this.state.unsavedTextareaMarkdown,
        onChange: this.onChangeTextarea,
        autoFocus: true
      })), this.state.showLinkDialog && this.renderLinkDialog({
        ...this.state.link,
        buttonEl: this.linkButtonRef?.current,
        onOk: this.insertLink,
        onClose: this.hideDialogs
      }), this.state.showImageDialog && /*#__PURE__*/React.createElement(AbMarkdownImageDialog, {
        alt: this.state.image.alt,
        url: this.state.image.url,
        onOk: this.insertImage,
        buttonEl: this.imageButtonRef?.current,
        onClose: this.hideDialogs
      }))
    })));
  }
  renderLinkDialog(props) {
    return /*#__PURE__*/React.createElement(AbMarkdownLinkDialog, props);
  }
  // default background color

  renderFloatingToolbar(editorState) {
    const currentInlineStyle = editorState.getCurrentInlineStyle();
    return /*#__PURE__*/React.createElement(AbPopup, {
      className: "ab-markdown-editor-popup-toolbar",
      stayInInitialPosition: true,
      modal: false,
      onKeyDown: event => event.key === "Escape",
      onClose: () => {/* closed only on unmount */
      },
      closeOnBackgroundClick: false,
      origin: this.sizedDiv ?? undefined,
      positioner: PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
      position: PopupPosition.ABOVE,
      draggable: true,
      draggableElementSelector: ".ab-local-toolbar",
      flushContent: true
    }, /*#__PURE__*/React.createElement(AbLocalToolbar, null, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.bold && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_bold(),
      icon: "absdk:text-bold",
      disabled: this.props.disabled,
      value: currentInlineStyle.has("BOLD"),
      onChange: this.textStyle.bind(this, "BOLD")
    }), !this.props.disabledEditorTools?.italic && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_italic(),
      icon: "absdk:text-italic",
      disabled: this.props.disabled,
      value: currentInlineStyle.has("ITALIC"),
      onChange: this.textStyle.bind(this, "ITALIC")
    }), !this.props.disabledEditorTools?.underline && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_underline(),
      icon: "absdk:text-underline",
      disabled: this.props.disabled,
      value: currentInlineStyle.has("UNDERLINE"),
      onChange: this.textStyle.bind(this, "UNDERLINE")
    }), !this.props.disabledEditorTools?.strikeThrough && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_strikethrough(),
      icon: "absdk:text-strikethrough",
      disabled: this.props.disabled,
      value: currentInlineStyle.has("STRIKETHROUGH"),
      onChange: this.textStyle.bind(this, "STRIKETHROUGH")
    }), !this.props.disabledEditorTools?.monospace && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_monospace(),
      icon: "absdk:text-monospace",
      disabled: this.props.disabled,
      value: currentInlineStyle.has("CODE"),
      onChange: this.textStyle.bind(this, "CODE")
    })), /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.popup && /*#__PURE__*/React.createElement(AbButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_popout(),
      disabled: this.props.disabled,
      onClick: this.showPopOut.bind(this),
      icon: "absdk:pop-out"
    }))));
  }
  showPopOut() {
    PopupUtils.showPopupExplicitly(/*#__PURE__*/React.createElement(AbMarkdownEditorPopout, {
      value: this.currentMarkdown,
      onOk: newVal => this.setNewMarkdown(newVal)
    }));
  }
  renderToolbar(editorState) {
    // How wide are we?  By default: very wide
    let width = 1000000;
    if (this.wrapperDiv) {
      width = this.wrapperDiv.clientWidth;
    }

    // If we're wider than 400px, use a wide toolbar.  Otherwise use narrow.  (Throw a CSS variable in there to keep Ben happy.)
    return width > parseInt(ElementUtils.getCss("--ab-markdown-wide-toolbar-min-width-private", "460"), 10) ? this.wideToolbar(editorState) : this.narrowToolbar(editorState);
  }
  wideToolbar(editorState) {
    const currentInlineStyle = editorState.getCurrentInlineStyle();
    const currentBlockType = Draft.RichUtils.getCurrentBlockType(editorState);
    const notTA = !this.state.showTextareaMarkdown;
    return /*#__PURE__*/React.createElement(AbLocalToolbar, null, /*#__PURE__*/React.createElement(AbLocalToolbarSection, null, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.bold && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_bold(),
      icon: "absdk:text-bold",
      disabled: this.props.disabled,
      value: notTA && currentInlineStyle.has("BOLD"),
      onChange: this.textStyle.bind(this, "BOLD")
    }), !this.props.disabledEditorTools?.italic && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_italic(),
      icon: "absdk:text-italic",
      disabled: this.props.disabled,
      value: notTA && currentInlineStyle.has("ITALIC"),
      onChange: this.textStyle.bind(this, "ITALIC")
    }), !this.props.disabledEditorTools?.underline && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_underline(),
      icon: "absdk:text-underline",
      disabled: this.props.disabled,
      value: notTA && currentInlineStyle.has("UNDERLINE"),
      onChange: this.textStyle.bind(this, "UNDERLINE")
    }), !this.props.disabledEditorTools?.strikeThrough && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_strikethrough(),
      icon: "absdk:text-strikethrough",
      disabled: this.props.disabled,
      value: notTA && currentInlineStyle.has("STRIKETHROUGH"),
      onChange: this.textStyle.bind(this, "STRIKETHROUGH")
    }), !this.props.disabledEditorTools?.monospace && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_monospace(),
      icon: "absdk:text-monospace",
      disabled: this.props.disabled,
      value: notTA && currentInlineStyle.has("CODE"),
      onChange: this.textStyle.bind(this, "CODE")
    })), /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.bulleted && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_unorderedList(),
      icon: "absdk:list",
      disabled: this.props.disabled,
      value: notTA && currentBlockType === "unordered-list-item",
      onChange: this.textBlock.bind(this, "unordered-list-item")
    }), !this.props.disabledEditorTools?.numbered && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_orderedList(),
      icon: "absdk:list-numbered",
      disabled: this.props.disabled,
      value: notTA && currentBlockType === "ordered-list-item",
      onChange: this.textBlock.bind(this, "ordered-list-item")
    })), /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.color && /*#__PURE__*/React.createElement(AbColorPicker, {
      icon: "absdk:text-color",
      tooltip: AbReactResources.markdownEditor_foregroundColorButton(),
      value: this.textColor,
      disabled: this.props.disabled,
      show12Colors: true,
      onChange: newColor => {
        this.textColor = newColor;
        this.colorPicked(newColor, "color");
      }
    }), !this.props.disabledEditorTools?.background && /*#__PURE__*/React.createElement(AbColorPicker, {
      icon: "absdk:text-highlight",
      tooltip: AbReactResources.markdownEditor_backgroundColorButton(),
      value: this.backgroundColor,
      disabled: this.props.disabled,
      show12Colors: true,
      onChange: newColor => {
        this.backgroundColor = newColor;
        this.colorPicked(newColor, "background");
      }
    })), /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.link && /*#__PURE__*/React.createElement(AbButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_createLink(),
      disabled: this.props.disabled,
      abRef: this.linkButtonRef,
      onClick: this.editLink,
      icon: "absdk:linked-item"
    }), !this.props.disabledEditorTools?.image && /*#__PURE__*/React.createElement(AbButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_createImage(),
      disabled: this.props.disabled,
      abRef: this.imageButtonRef,
      onClick: this.editImage,
      icon: "absdk:photo"
    }))), /*#__PURE__*/React.createElement(AbLocalToolbarSection, null, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.textMode && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      icon: "absdk:text-markdown",
      tooltip: AbReactResources.markdownEditor_editMarkdown(),
      dataTestId: "edit-markdown-button",
      value: this.state.showTextareaMarkdown,
      disabled: this.props.disabled,
      onChange: this.editMarkdown
    }))));
  }
  narrowToolbar(editorState) {
    const currentInlineStyle = editorState.getCurrentInlineStyle();
    const currentBlockType = Draft.RichUtils.getCurrentBlockType(editorState);
    const notTA = !this.state.showTextareaMarkdown;
    return /*#__PURE__*/React.createElement(AbLocalToolbar, null, /*#__PURE__*/React.createElement(AbLocalToolbarSection, null, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, /*#__PURE__*/React.createElement(AbMenuButton, {
      className: "ab-no-border-button",
      icon: "absdk:text-font",
      tooltip: AbReactResources.markdownEditor_fontMenu(),
      menuProps: {
        className: "ab-markdown-menu-popup"
      },
      menuItems: [!this.props.disabledEditorTools?.bold && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-no-border-button ab-toggle-button", notTA && currentInlineStyle.has("BOLD") && "ab-toggled"]),
        label: AbReactResources.markdownEditor_bold(),
        icon: "absdk:text-bold",
        disabled: this.props.disabled,
        onClick: this.textStyle.bind(this, "BOLD")
      }, !this.props.disabledEditorTools?.italic && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-no-border-button ab-toggle-button", notTA && currentInlineStyle.has("ITALIC") && "ab-toggled"]),
        label: AbReactResources.markdownEditor_italic(),
        icon: "absdk:text-italic",
        disabled: this.props.disabled,
        onClick: this.textStyle.bind(this, "ITALIC")
      }, !this.props.disabledEditorTools?.underline && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-no-border-button ab-toggle-button", notTA && currentInlineStyle.has("UNDERLINE") && "ab-toggled"]),
        label: AbReactResources.markdownEditor_underline(),
        icon: "absdk:text-underline",
        disabled: this.props.disabled,
        onClick: this.textStyle.bind(this, "UNDERLINE")
      }, !this.props.disabledEditorTools?.strikeThrough && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-no-border-button ab-toggle-button", notTA && currentInlineStyle.has("STRIKETHROUGH") && "ab-toggled"]),
        label: AbReactResources.markdownEditor_strikethrough(),
        icon: "absdk:text-strikethrough",
        disabled: this.props.disabled,
        onClick: this.textStyle.bind(this, "STRIKETHROUGH")
      }, !this.props.disabledEditorTools?.monospace && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-no-border-button ab-toggle-button", notTA && currentInlineStyle.has("CODE") && "ab-toggled"]),
        label: AbReactResources.markdownEditor_monospace(),
        icon: "absdk:text-monospace",
        disabled: this.props.disabled,
        onClick: this.textStyle.bind(this, "CODE")
      }]
    }), /*#__PURE__*/React.createElement(AbMenuButton, {
      className: "ab-no-border-button",
      icon: "absdk:text-align-justify",
      tooltip: AbReactResources.markdownEditor_listMenu(),
      menuItems: [!this.props.disabledEditorTools?.bulleted && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-toggle-button", currentBlockType === "unordered-list-item" && "ab-toggled"]),
        disabled: this.props.disabled,
        onClick: this.textBlock.bind(this, "unordered-list-item"),
        icon: "absdk:list",
        label: AbReactResources.markdownEditor_unorderedList()
      }, !this.props.disabledEditorTools?.numbered && {
        className: ReactUtils.classNamesString(["ab-markdown-menu-button ab-toggle-button", currentBlockType === "ordered-list-item" && "ab-toggled"]),
        disabled: this.props.disabled,
        onClick: this.textBlock.bind(this, "ordered-list-item"),
        icon: "absdk:list-numbered",
        label: AbReactResources.markdownEditor_orderedList()
      }]
    }), /*#__PURE__*/React.createElement(AbPopupButton, {
      className: "ab-no-border-button",
      icon: "absdk:text-color",
      abRef: this.colorPopupButtonRef,
      tooltip: AbReactResources.markdownEditor_colorMenu(),
      popupContents: /*#__PURE__*/React.createElement(React.Fragment, null, !this.props.disabledEditorTools?.color && /*#__PURE__*/React.createElement(AbColorPicker, {
        icon: "absdk:text-color",
        value: this.textColor,
        disabled: this.props.disabled,
        show12Colors: true,
        label: AbReactResources.markdownEditor_foregroundColorMenu(),
        className: "ab-markdown-narrow-menu",
        onChange: newColor => {
          this.textColor = newColor;
          this.colorPicked(newColor, "color");
        }
      }), !this.props.disabledEditorTools?.background && /*#__PURE__*/React.createElement(AbColorPicker, {
        icon: "absdk:text-highlight",
        value: this.backgroundColor,
        disabled: this.props.disabled,
        show12Colors: true,
        label: AbReactResources.markdownEditor_backgroundColorMenu(),
        className: "ab-markdown-narrow-menu",
        onChange: newColor => {
          this.backgroundColor = newColor;
          this.colorPicked(newColor, "background");
        }
      }))
    }), !this.props.disabledEditorTools?.link && /*#__PURE__*/React.createElement(AbButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_createLink(),
      disabled: this.props.disabled,
      abRef: this.linkButtonRef,
      onClick: this.editLink,
      icon: "absdk:linked-item"
    }), !this.props.disabledEditorTools?.image && /*#__PURE__*/React.createElement(AbButton, {
      className: "abui ab-no-border-button",
      tooltip: AbReactResources.markdownEditor_createImage(),
      disabled: this.props.disabled,
      abRef: this.imageButtonRef,
      onClick: this.editImage,
      icon: "absdk:photo"
    }))), /*#__PURE__*/React.createElement(AbLocalToolbarSection, null, /*#__PURE__*/React.createElement(AbLocalToolbarGroup, null, !this.props.disabledEditorTools?.textMode && /*#__PURE__*/React.createElement(AbToggleButton, {
      className: "abui ab-no-border-button",
      icon: "absdk:text-markdown",
      tooltip: AbReactResources.markdownEditor_editMarkdown(),
      disabled: this.props.disabled,
      dataTestId: "edit-markdown-button",
      value: this.state.showTextareaMarkdown,
      onChange: this.editMarkdown
    }))));
  }
  componentDidUpdate() {
    //Sometimes the markup adds an extra space at the end, therefore making these not equal and causing
    //the cursor to jump around during editing. Comparing the trimmed strings fixes the issue.
    const propsValue = this.props.value || ""; // empty if early
    const markdownValue = this.currentMarkdown || ""; //
    if (propsValue.trim() !== markdownValue.trim()) {
      // container has changed prop so DraftEditor's markdown doesn't match, so update the DraftEditor.
      this.setNewMarkdown(this.props.value);
    } else if (this.focusOnComponentUpdate) {
      this.focusOnComponentUpdate = false;
      if (this.state.showTextareaMarkdown) FocusUtils.setFocus(this.textareaMarkdownRef);else this.draftEditor?.focus();
    }
  }

  // DraftJS Decorators scan the text, and call this function with contentBlock set to each Block.
  // This function must call the callback when it finds something interesting.
  // When it does, the Decorator creates a React.Component in that spot.
  static findLinkEntities(contentBlock, callback, contentState) {
    AbMarkdownEditor.findLocalEntities("LINK", contentBlock, callback, contentState);
  }
  static findImageEntities(contentBlock, callback, contentState) {
    AbMarkdownEditor.findLocalEntities("IMAGE", contentBlock, callback, contentState);
  }
  static findPersonEntities(contentBlock, callback, contentState) {
    AbMarkdownEditor.findLocalEntities("PERSON", contentBlock, callback, contentState);
  }
  static findAutocompleteEntities(contentBlock, callback, contentState) {
    AbMarkdownEditor.findLocalEntities("AUTOCOMPLETE_MARKER", contentBlock, callback, contentState);
  }
  static findLocalEntities(entityType, contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      return entityKey !== null && contentState.getEntity(entityKey).getType() === entityType;
    }, callback);
  }
}
_AbMarkdownEditor = AbMarkdownEditor;
// draftToMarkdown converts Draft style items to markdown.  For example, UNDERLINE-open is "++".
// We add the color style conversions in AbColorPicker.addColorsToMarkdownOptions.
_defineProperty(AbMarkdownEditor, "draftToMarkdownOptions", void 0);
// markdownToDraft converts markdown back to Draft.
// When the markdown sees a "del" , it generates a Style of "STRIKETHROUGH"
// When the markdown sees an "ins", it generates a Style of "UNDERLINE"
_defineProperty(AbMarkdownEditor, "markdownToDraftBlockStyles", {
  del_open: "STRIKETHROUGH",
  ins_open: "UNDERLINE"
});
_defineProperty(AbMarkdownEditor, "LOG", LogUtils.getLogger(__filename));
_defineProperty(AbMarkdownEditor, "draftStyleToTextAreaMarkdown", {
  "BOLD": "bold",
  "CODE": "code-inline",
  "ITALIC": "italic",
  "STRIKETHROUGH": "strike-through",
  "UNDERLINE": "underline"
});
_defineProperty(AbMarkdownEditor, "draftBlockToTextAreaMarkdown", {
  "unordered-list-item": "unordered-list",
  "ordered-list-item": "ordered-list",
  "code-block": "code-block"
});
/**
 *   Keyboard shortcuts.<br>
 *   Default values:<br>
 *   CTRL-B bold, CTRL-D delete, CTRL-H backspace, CTRL-I italic, CTRL-J code, CTRL-M split block, CTRL-U underline, CTRL-Y redo, CTRL-Z undo.
 *   Custom values<br>
 *   SHIFT-enter insert-line-break.
 *   When in autocomplete mode, then arrows, escape, enter and tab all change.  Other keys are turned off.
 */
_defineProperty(AbMarkdownEditor, "autocompleteKeyCommandMap", {
  "ArrowUp": AbMarkdownCommands.AUTOCOMPLETE_UP_ARROW,
  "ArrowDown": AbMarkdownCommands.AUTOCOMPLETE_DOWN_ARROW,
  "Escape": AbMarkdownCommands.AUTOCOMPLETE_EXIT,
  "Enter": AbMarkdownCommands.AUTOCOMPLETE_ENTER,
  "Tab": AbMarkdownCommands.AUTOCOMPLETE_ENTER,
  "Home": AbMarkdownCommands.HANDLED,
  "End": AbMarkdownCommands.HANDLED,
  "PageUp": AbMarkdownCommands.HANDLED,
  "PageDown": AbMarkdownCommands.HANDLED
});
_defineProperty(AbMarkdownEditor, "textareaMarkdownOptions", {
  preferredItalicSyntax: "_",
  boldPlaceholder: "",
  italicPlaceholder: "",
  strikeThroughPlaceholder: "",
  codeInlinePlaceholder: "",
  enableLinkPasteExtension: false
});
_defineProperty(AbMarkdownEditor, "underlineHandler", ({
  cursor
}) => {
  cursor.wrap("++");
});
_defineProperty(AbMarkdownEditor, "colorHandler", ({
  cursor
}, newColor, colorOrBackground) => {
  cursor.wrap([`{${colorOrBackground}: ${newColor}}`, `{${colorOrBackground}}`]);
});
_defineProperty(AbMarkdownEditor, "textareaMarkdownCommands", [{
  name: "underline",
  handler: _AbMarkdownEditor.underlineHandler
}, {
  name: "color",
  handler: _AbMarkdownEditor.colorHandler
}]);
function AbMarkdownEditorPopout(props) {
  const [popoutVal, setPopoutVal] = React.useState(props.value);
  return /*#__PURE__*/React.createElement(AbDialog, {
    style: {
      height: "80vh",
      width: "80vw"
    },
    className: "ab-markdown-editor-popout",
    title: AbReactResources.markdownEditor_popoutTitle(),
    closeButton: false,
    onOk: async () => {
      props.onOk(popoutVal);
      return true;
    },
    cancelButton: true,
    resizable: true
  }, /*#__PURE__*/React.createElement(AbMarkdownEditor, {
    value: popoutVal,
    onChange: x => setPopoutVal(x)
  }));
}
//# sourceMappingURL=AbMarkdownEditor.js.map
